"use client";

import { useState, useRef, useEffect, useCallback } from "react";
import { TRYON_SERVER_URL } from "../config";
import { useDispatch, useSelector } from "react-redux";
import { selectAccessToken, selectAuthenticated, selectUserData } from "../selectors";
import { setUserData } from "../store/AuthActions";
import { fetchUser } from "../Utils/UserUtils";
import GalleryModal from "../components/GalleryModal";
import { useNavigate } from "react-router-dom";
import SignInPrompt from "../components/SignInPrompt";
import {
  ReactCompareSlider,
  ReactCompareSliderImage,
} from "react-compare-slider";
import { uploadExperimentImage } from "../Utils/ExperimentImageUtils";
import {
  createVideoGenerationService,
  cleanupVideoBlobUrl,
  videoCredits,
} from "../Utils/VideoGenerationUtils";
import { creditsForImage, generateImage, loadGeneratedImage } from "../Utils/ImageGenerateUtils";
import Spinner from "../components/Spinner";
import ChatInterface from "../components/ChatInterface";
import PromptContainer from "../components/PromptContainer";
import ChangeBackground from "../components/ChangeBackground";
import Resize from "../components/Resize";
import GenerationSettings from "../components/GenerationSettings";
import { image } from "@tensorflow/tfjs";
import { CaptionsIcon, CheckCheckIcon, CircleXIcon, CloudUploadIcon, CodeIcon, Columns2Icon, CropIcon, DownloadIcon, ImageDownIcon, ImageIcon, InfoIcon, LayersIcon, MessagesSquareIcon, Redo2Icon, ScalingIcon, Trash2Icon, TypeIcon, Undo2Icon, WandSparklesIcon } from "lucide-react";

// 🟢 ADD THIS FUNCTION HERE (top inside component)
const fetchImageWithAuth = async (url, accessToken) => {
  const response = await fetch(url, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });

  const blob = await response.blob();
  return URL.createObjectURL(blob);
};

const Studio = ({
  selectedPrompt,
  setShowPromptGalleryModal,
  onSelectPrompt,
  showPromptGalleryModal,
}) => {
  const [zoom, setZoom] = useState(100);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [containerHeight, setContainerHeight] = useState("100vh");
  const containerRef = useRef(null);
  const imageRef = useRef(null); // This ref will now point to either <img> or <video>
  const [showGalleryModal, setShowGalleryModal] = useState(false);
  const [selectedObj, setSelectedObj] = useState(null);
  const accessToken = useSelector(selectAccessToken);
  const userData = JSON.parse(useSelector(selectUserData) || "null");
  const dispatch = useDispatch();
  const REMOVE_BG_CREDITS = 4;
  const UPSCALE_CREDITS = 48;
  const CHAT_CREDITS = 1;
  const editCredits = creditsForImage("2K", 1);

  const requireCredits = (amount) => {
    const balance = Number(userData?.credits);
    if (!Number.isFinite(balance) || balance < amount) {
      navigate(`/credits?buy_credits=true&num_of_credits=${amount}`);
      return false;
    }
    return true;
  };

  const refreshCredits = async () => {
    const user = await fetchUser(accessToken);
    if (user.status === "success") {
      dispatch(setUserData(JSON.stringify(user.data)));
    }
  };
  const authenticated = useSelector(selectAuthenticated);
  const navigate = useNavigate();
  const [showToastErrorMessage, setShowToastErrorMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [errorStatus, setErrorStatus] = useState("");

  // Changed from currentImage to currentMediaUrl
  const [currentMediaUrl, setCurrentMediaUrl] = useState(null);
  const [isMediaTypeVideo, setIsMediaTypeVideo] = useState(false); // New state for video type

  const [imageWithTransparentBackground, setImageWithTransparentBackground] =
    useState(null);
  // Undo/Redo stacks now store objects { url: string, isVideo: boolean }
  const [redoStack, setRedoStack] = useState([]);
  const [undoStack, setUndoStack] = useState([]);
  const [isCompareMode, setIsCompareMode] = useState(false);
  const [showToastSuccessMessage, setShowToastSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [processingMessage, setProcessingMessage] =
    useState("Processing Image");
  const [processingSubMessage, setProcessingSubMessage] = useState(
    "This might take a few moments..."
  );
  const [processingType, setProcessingType] = useState("loading"); // 'loading', 'info', 'warning'
  const [isCropping, setIsCropping] = useState(false);
  const [cropBox, setCropBox] = useState({ x: 0, y: 0, width: 0, height: 0 });
  const [cropStartPos, setCropStartPos] = useState({ x: 0, y: 0 });
  const [isDraggingCrop, setIsDraggingCrop] = useState(false);
  const [resizeHandle, setResizeHandle] = useState(null);
  const cropCanvasRef = useRef(null);
  const cropContainerRef = useRef(null);
  const [extraPadding, setExtraPadding] = useState(0);
  const [isImageLoaded, setIsImageLoaded] = useState(false); // Still relevant for images
  const [isCaptioning, setIsCaptioning] = useState(false);
  const [showCaptionOptions, setShowCaptionOptions] = useState(false);
  const [chatInput, setChatInput] = useState("");
  const [showChat, setShowChat] = useState(false);
  const [chatResponses, setChatResponses] = useState([]);
  const chatContainerRef = useRef(null);
  const [isChatProcessing, setIsChatProcessing] = useState(false);
  const [showCaptionRequestResponse, setShowCaptionRequestResponse] =
    useState(false);
  const [captionRequestResponse, setCaptionRequestResponse] = useState("");
  // Add new state for copy toast
  const [showCopyToast, setShowCopyToast] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [defaultGenerationSettings, setDefaultGenerationSettings] = useState({
    seed: 42,
    safety_tolerance: 2,
    output_format: "jpeg",
    aspect_ratio: "default",
    prompt_upsampling: false,
    model: "flux-kontext-pro", // 'flux-kontext-pro' or 'flux-kontext-max',
    prompt: "",
  });
  const [addBackgroundMode, setAddBackgroundMode] = useState(false);
  const [isBackgroundRemoved, setIsBackgroundRemoved] = useState(false);
  const [isBackgroundAdded, setIsBackgroundAdded] = useState(false);
  const [resizeImage, setResizeImage] = useState(false);
  // Separate settings for image and video generation
  const [imageSettings, setImageSettings] = useState({
    seed: 42,
    safety_tolerance: 2,
    output_format: "jpeg",
    aspect_ratio: "default",
    prompt_upsampling: false,
    model: "flux-kontext-pro", // 'flux-kontext-pro' or 'flux-kontext-max'
    prompt: "",
  });

  const [videoSettings, setVideoSettings] = useState({
    model: "sora",
    aspect_ratio: "16:9",
    resolution: "1080p",
    duration: "8",
    prompt: "",
  });

  const [selectedMode, setSelectedMode] = useState("image"); // 'image' or 'video'

  // Computed properties for current settings
  const currentSettings =
    selectedMode === "image" ? imageSettings : videoSettings;
  const setCurrentSettings =
    selectedMode === "image" ? setImageSettings : setVideoSettings;
  const settingsRef = useRef(null);

  const calculateRemainingHeight = () => {
    const navbar = document.getElementById("navbar");
    if (navbar) {
      const navbarHeight = navbar.offsetHeight;
      const remainingHeight = `calc(100vh - ${navbarHeight}px)`;
      console.log(remainingHeight);
      setContainerHeight(remainingHeight);
    }
  };

  useEffect(() => {
    calculateRemainingHeight();
    // Recalculate on window resize
    window.addEventListener("resize", calculateRemainingHeight);
    return () => window.removeEventListener("resize", calculateRemainingHeight);
  }, [currentMediaUrl]); // Dependency changed to currentMediaUrl

  // Cleanup blob URLs to prevent memory leaks
  useEffect(() => {
    // Store current URL to cleanup when it changes
    const previousUrl = currentMediaUrl;

    return () => {
      // Cleanup previous blob URL when currentMediaUrl changes or component unmounts
      if (previousUrl) {
        cleanupVideoBlobUrl(previousUrl);
      }
    };
  }, [currentMediaUrl]);

  const calculateInitialZoom = (
    imgWidth,
    imgHeight,
    containerWidth,
    containerHeight
  ) => {
    // Calculate the scale to fit the image within viewport
    const scaleX = (containerWidth - 40) / imgWidth;
    const scaleY = (containerHeight - 40) / imgHeight;
    // For high-resolution images, we want to ensure they're visible
    // but not too small. Let's set a minimum scale based on the image size
    const minScale = 0.5; // This ensures the image is at least 50% of its original size
    const maxScale = 1.5; // This ensures the image is not larger than 150% of its original size
    // Calculate the scale that would fit the image in the viewport
    let scale = Math.min(scaleX, scaleY);
    // If the image is very large (like 4K), we'll use a different approach
    if (imgWidth > 1920 || imgHeight > 1080) {
      // For 4K images, we'll use a fixed scale that ensures good visibility
      scale = Math.max(minScale, Math.min(maxScale, scale));
    } else {
      // For smaller images, we'll use the calculated scale
      scale = Math.min(scale, 1);
    }
    // Convert scale to zoom percentage
    const calculatedZoom = Math.floor(scale * 100);
    console.log("calculatedZoom", calculatedZoom);
    // Set zoom limits
    const minZoom = 50;
    const maxZoom = 150;
    // Ensure zoom stays within bounds
    const boundedZoom = Math.min(Math.max(calculatedZoom, minZoom), maxZoom);
    console.log("boundedZoom", boundedZoom);
    // If calculated zoom is too low, we'll adjust the container padding instead
    if (calculatedZoom < minZoom) {
      // Calculate how much padding we need to add to maintain minimum zoom
      const paddingScale = minZoom / calculatedZoom;
      const extraPadding = Math.max(
        0,
        (containerWidth * (paddingScale - 1)) / 2
      );
      return {
        zoom: minZoom,
        extraPadding: Math.floor(extraPadding),
      };
    }
    // If calculated zoom is too high, we'll adjust the container padding instead
    if (calculatedZoom > maxZoom) {
      // Calculate how much padding we need to add to maintain maximum zoom
      const paddingScale = maxZoom / calculatedZoom;
      const extraPadding = Math.max(
        0,
        (containerWidth * (paddingScale - 1)) / 2
      );
      return {
        zoom: maxZoom,
        extraPadding: Math.floor(extraPadding),
      };
    }
    return {
      zoom: boundedZoom,
      extraPadding: 0,
    };
  };

  const handleZoomChange = (newZoom) => {
    console.log("handleZoomChange called with:", newZoom);
    // Ensure zoom stays within bounds (50% to 150%)
    const zoomLevel = Math.min(Math.max(newZoom, 50), 150);
    console.log("Setting zoom from handleZoomChange:", zoomLevel);
    // If in cropping mode, exit cropping mode when zoom changes
    if (isCropping) {
      setIsCropping(false);
    }
    // Update zoom state
    setZoom(zoomLevel);
    // Ensure scroll position stays centered after zoom
    if (containerRef.current && imageRef.current) {
      const container = containerRef.current;
      // Get current view center as percentage of total scroll area
      const prevScrollWidth = container.scrollWidth;
      const prevScrollHeight = container.scrollHeight;
      const viewWidth = container.clientWidth;
      const viewHeight = container.clientHeight;
      // Calculate current center position percentages
      const prevCenterXPercent =
        (container.scrollLeft + viewWidth / 2) / prevScrollWidth;
      const prevCenterYPercent =
        (container.scrollTop + viewHeight / 2) / prevScrollHeight;
      // Wait for a tick to let the browser update the DOM with new zoom
      requestAnimationFrame(() => {
        // Apply the same center position percentages to the new scroll area
        const newScrollWidth = container.scrollWidth;
        const newScrollHeight = container.scrollHeight;
        // Calculate new scroll positions
        const newScrollLeft =
          prevCenterXPercent * newScrollWidth - viewWidth / 2;
        const newScrollTop =
          prevCenterYPercent * newScrollHeight - viewHeight / 2;
        // Apply new scroll positions
        container.scrollLeft = newScrollLeft;
        container.scrollTop = newScrollTop;
      });
    }
  };

  // Add effect to handle image load and initial zoom
  useEffect(() => {
    if (imageRef.current && !isMediaTypeVideo) {
      // Only run for images
      const img = imageRef.current;
      if (img.complete) {
        console.log("Image already loaded, calculating zoom");
        handleImageLoadAndZoom();
      } else {
        console.log("Setting up image load handler");
        img.onload = handleImageLoadAndZoom;
      }
    }
  }, [currentMediaUrl, isMediaTypeVideo]); // Depend on currentMediaUrl and isMediaTypeVideo

  const handleImageLoadAndZoom = () => {
    console.log("Image load handler called");
    if (containerRef.current && imageRef.current) {
      const container = containerRef.current;
      const img = imageRef.current;
      console.log(
        "Image loaded - natural dimensions:",
        img.naturalWidth,
        img.naturalHeight
      );
      const { zoom: newZoom, extraPadding: newPadding } = calculateInitialZoom(
        img.naturalWidth,
        img.naturalHeight,
        container.clientWidth,
        container.clientHeight
      );
      console.log("Setting initial zoom from image load:", newZoom);
      setZoom(newZoom);
      setExtraPadding(newPadding);
      setIsImageLoaded(true);
      // Center the image
      setTimeout(() => {
        container.scrollLeft =
          (container.scrollWidth - container.clientWidth) / 2;
        container.scrollTop =
          (container.scrollHeight - container.clientHeight) / 2;
      }, 50);
    }
  };

  const handleMouseDown = (e) => {
    if (zoom > 100 && !isCropping && !isMediaTypeVideo) {
      // Only for images
      // Prevent default behavior to avoid text selection during drag
      e.preventDefault();
      setIsDragging(true);
      setDragStart({
        x: e.clientX,
        y: e.clientY,
      });
      // Change cursor style to indicate grabbing
      if (containerRef.current) {
        containerRef.current.style.cursor = "grabbing";
      }
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging && zoom > 100 && !isCropping && !isMediaTypeVideo) {
      // Only for images
      e.preventDefault();
      const deltaX = e.clientX - dragStart.x;
      const deltaY = e.clientY - dragStart.y;
      if (containerRef.current) {
        const container = containerRef.current;
        // Use a multiplier to make dragging feel more responsive at higher zoom levels
        const dragSpeed = zoom > 150 ? 1.5 : 1;
        // Adjust the scroll position based on mouse movement
        container.scrollLeft -= deltaX * dragSpeed;
        container.scrollTop -= deltaY * dragSpeed;
        // Update drag start position for next move event
        setDragStart({
          x: e.clientX,
          y: e.clientY,
        });
      }
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    // Reset cursor style
    if (containerRef.current) {
      containerRef.current.style.cursor =
        zoom > 100 && !isMediaTypeVideo ? "grab" : "default"; // Only for images
    }
  };

  // Add an effect to update cursor style when zoom changes
  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.style.cursor =
        zoom > 100 && !isCropping && !isMediaTypeVideo ? "grab" : "default"; // Only for images
    }
  }, [zoom, isCropping, isMediaTypeVideo]);

  // Add a function to save media state to history
  const saveMediaToHistory = (mediaUrl, isVideo) => {
    // Add current media to undo stack if it exists
    if (currentMediaUrl) {
      setUndoStack((prev) => [
        ...prev,
        { url: currentMediaUrl, isVideo: isMediaTypeVideo },
      ]);
    }
    // Clear redo stack when new media is added
    setRedoStack([]);
    // Update current media
    setCurrentMediaUrl(mediaUrl);
    setIsMediaTypeVideo(isVideo);

    // Disable compare mode when adding a new media to history
    if (isCompareMode) {
      setIsCompareMode(false);
    }
    if (isCaptioning) {
      setIsCaptioning(false);
      setShowCaptionOptions(false);
    }
  };

  // Handle undo operation
  const handleUndo = () => {
    // Exit crop mode if active
    if (isCropping) {
      setIsCropping(false);
    }
    if (undoStack.length > 0) {
      // Get the last media from undo stack
      const previousMedia = undoStack[undoStack.length - 1];
      // Add current media to redo stack if it exists
      if (currentMediaUrl) {
        setRedoStack((prev) => [
          ...prev,
          { url: currentMediaUrl, isVideo: isMediaTypeVideo },
        ]);
      }
      // Update undo stack and current media
      setUndoStack((prev) => prev.slice(0, -1));
      setCurrentMediaUrl(previousMedia.url);
      setIsMediaTypeVideo(previousMedia.isVideo);
      // Reset transparent background image (only relevant for images)
      setImageWithTransparentBackground(null);
      // Disable compare mode when undoing
      if (isCompareMode) {
        setIsCompareMode(false);
      }
      if (isCaptioning) {
        setIsCaptioning(false);
        setShowCaptionOptions(false);
      }
    }
  };

  // Handle redo operation
  const handleRedo = () => {
    // Exit crop mode if active
    if (isCropping) {
      setIsCropping(false);
    }
    if (redoStack.length > 0) {
      // Get the last media from redo stack
      const nextMedia = redoStack[redoStack.length - 1];
      // Add current media to undo stack if it exists
      if (currentMediaUrl) {
        setUndoStack((prev) => [
          ...prev,
          { url: currentMediaUrl, isVideo: isMediaTypeVideo },
        ]);
      }
      // Update redo stack and current media
      setRedoStack((prev) => prev.slice(0, -1));
      setCurrentMediaUrl(nextMedia.url);
      setIsMediaTypeVideo(nextMedia.isVideo);
      // Reset transparent background image (only relevant for images)
      setImageWithTransparentBackground(null);
      // Disable compare mode when redoing
      if (isCompareMode) {
        setIsCompareMode(false);
      }
      if (isCaptioning) {
        setIsCaptioning(false);
        setShowCaptionOptions(false);
      }
    }
  };

  const handleFileSelected = (event) => {
    const file = event.target.files[0];
    if (file) {
      console.log("File selected, resetting image loaded state");
      setIsImageLoaded(false);
      const mediaUrl = URL.createObjectURL(file);
      const isVideo = file.type.startsWith("video/");

      setCurrentMediaUrl(mediaUrl);
      setIsMediaTypeVideo(isVideo);
      saveMediaToHistory(mediaUrl, isVideo);

      // Reset image-specific states if a video is loaded
      if (isVideo) {
        setImageWithTransparentBackground(null);
        setIsCropping(false);
        setIsCompareMode(false);
        setAddBackgroundMode(false);
        setResizeImage(false);
        setIsCaptioning(false);
        setShowCaptionOptions(false);
        setShowChat(false);
        setChatInput("");
        setChatResponses([]);
        setZoom(100); // Reset zoom for video
      } else {
        setImageWithTransparentBackground(null);
      }
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragging(false);
    const file = event.dataTransfer.files[0];
    if (file) {
      console.log("File dropped, resetting image loaded state");
      setIsImageLoaded(false);
      const mediaUrl = URL.createObjectURL(file);
      const isVideo = file.type.startsWith("video/");

      setCurrentMediaUrl(mediaUrl);
      setIsMediaTypeVideo(isVideo);
      saveMediaToHistory(mediaUrl, isVideo);

      if (isVideo) {
        setImageWithTransparentBackground(null);
        setIsCropping(false);
        setIsCompareMode(false);
        setAddBackgroundMode(false);
        setResizeImage(false);
        setIsCaptioning(false);
        setShowCaptionOptions(false);
        setShowChat(false);
        setChatInput("");
        setChatResponses([]);
        setZoom(100);
      }
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleImageUpload = async (
    file,
    action,
    backgroundImage = null,
    backgroundColor = null,
    gradientPattern = true
  ) => {
    if (isMediaTypeVideo) {
      console.warn("handleImageUpload called with a video. Aborting.");
      setIsProcessing(false);
      return;
    }

    const formData = new FormData();
    formData.append("image", file);
    formData.append("action", action);
    try {
      const responseImage = await fetch(`${TRYON_SERVER_URL}/api/v1/studio/`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        body: formData,
      });
      if (responseImage.status !== 200) {
        const payload = await responseImage.json().catch(() => null);
        setShowToastErrorMessage(true);
        setErrorMessage(payload?.message || payload?.error || "The image could not be processed.");
        setErrorStatus("Error");
        setIsProcessing(false);
        return;
      }
      const imageBlob = await responseImage.blob();
      const imageUrl = URL.createObjectURL(imageBlob);
      if (action === "remove_background") {
        setIsBackgroundRemoved(true);
        setImageWithTransparentBackground(imageUrl);
        await addBackground(
          imageUrl,
          backgroundColor,
          gradientPattern,
          backgroundImage
        );
      } else {
        setCurrentMediaUrl(imageUrl);
        setIsProcessing(false);
        saveMediaToHistory(imageUrl, false);
        setImageWithTransparentBackground(null);
        await refreshCredits();
      }
    } catch (error) {
      console.error("Error in handleImageUpload:", error);
      setShowToastErrorMessage(true);
      setErrorMessage("Error processing image. Please try again.");
      setErrorStatus("Error");
      setIsProcessing(false);
    }
  };

  /**
   * Add a white background to the image
   * @param {string} imageUrl - The URL of the image to add a background to
   */
  const addBackground = async (
    imageUrl,
    backgroundColor = null,
    gradientPattern = true,
    backgroundImage = null
  ) => {
    if (isMediaTypeVideo) {
      console.warn("addBackground called with a video. Aborting.");
      setIsProcessing(false);
      return;
    }

    console.log(
      "addBackground:",
      imageUrl,
      backgroundColor,
      gradientPattern,
      backgroundImage
    );
    if (backgroundImage) {
      setIsProcessing(true);
      setProcessingMessage("Adding Background");
      setProcessingSubMessage("Adding background to your image...");
      setProcessingType("loading");
    }
    // load image and draw it on the canvas
    const img = new Image();
    img.crossOrigin = "anonymous"; // Add CORS support
    img.onload = () => {
      // Create a canvas with the same dimensions as the image
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext("2d");
      if (backgroundColor) {
        // Fill the canvas with white
        ctx.fillStyle = backgroundColor;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        setIsBackgroundAdded(true);
      }
      if (gradientPattern) {
        // Create a pattern canvas
        const patternCanvas = document.createElement("canvas");
        patternCanvas.width = 20;
        patternCanvas.height = 20;
        const patternCtx = patternCanvas.getContext("2d");
        // Draw the gradient pattern
        patternCtx.fillStyle = "#ccc";
        patternCtx.fillRect(0, 0, 10, 10);
        patternCtx.fillRect(10, 10, 10, 10);
        // Create pattern and fill the main canvas
        const pattern = ctx.createPattern(patternCanvas, "repeat");
        ctx.fillStyle = pattern;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }
      // draw the background image on the canvas
      if (backgroundImage) {
        console.log("backgroundImage:", backgroundImage);
        // Use the image URL directly since it's already a blob URL
        const bgImage = new Image();
        bgImage.crossOrigin = "anonymous";
        bgImage.src = backgroundImage;
        bgImage.onload = () => {
          // Calculate dimensions to cover the entire canvas while maintaining aspect ratio
          const bgImageWidth = bgImage.width;
          const bgImageHeight = bgImage.height;
          const canvasRatio = canvas.width / canvas.height;
          const imageRatio = bgImageWidth / bgImageHeight;
          let drawWidth,
            drawHeight,
            offsetX = 0,
            offsetY = 0;
          if (imageRatio > canvasRatio) {
            // Image is wider than canvas ratio
            drawHeight = canvas.height;
            drawWidth = drawHeight * imageRatio;
            offsetX = (canvas.width - drawWidth) / 2;
          } else {
            // Image is taller than canvas ratio
            drawWidth = canvas.width;
            drawHeight = drawWidth / imageRatio;
            offsetY = (canvas.height - drawHeight) / 2;
          }
          // Draw the background image to cover the entire canvas
          ctx.drawImage(bgImage, offsetX, offsetY, drawWidth, drawHeight);
          // draw the main image on the canvas
          ctx.drawImage(img, 0, 0);
          // convert the canvas to a data URL
          const finalImageUrl = canvas.toDataURL("image/png");
          setCurrentMediaUrl(finalImageUrl); // Update currentMediaUrl
          setIsProcessing(false);
          // Save to history
          saveMediaToHistory(finalImageUrl, false); // It's an image
          setIsBackgroundAdded(true);
        };
      } else {
        // draw the main image on the canvas
        ctx.drawImage(img, 0, 0);
        const finalImageUrl = canvas.toDataURL("image/png");
        setCurrentMediaUrl(finalImageUrl); // Update currentMediaUrl
        setIsProcessing(false);
        // Save to history
        saveMediaToHistory(finalImageUrl, false); // It's an image
      }
    };
    img.src = imageUrl;
  };

  const handleTool = async (
    action,
    backgroundImage = null,
    backgroundColor = null,
    gradientPattern = true
  ) => {
    if (isMediaTypeVideo) {
      setShowToastErrorMessage(true);
      setErrorMessage("This tool is not available for videos.");
      setErrorStatus("Unsupported Media");
      return;
    }

    setIsProcessing(true);
    setIsBackgroundRemoved(false);
    // Exit crop mode if active
    if (isCropping) {
      setIsCropping(false);
    }
    if (isCaptioning) {
      setIsCaptioning(false);
      setShowCaptionOptions(false);
    }
    if (isCompareMode) {
      setIsCompareMode(false);
    }
    if (showChat) {
      setShowChat(false);
    }
    if (
      action === "remove_background" &&
      imageWithTransparentBackground !== null
    ) {
      addBackground(
        imageWithTransparentBackground,
        backgroundColor,
        gradientPattern,
        backgroundImage
      );
      return;
    }
    // Reset transparent background image for any tool action
    setImageWithTransparentBackground(null);
    // Set appropriate processing messages based on action
    const toolCredits = action === "upscale_image" ? UPSCALE_CREDITS : action === "remove_background" ? REMOVE_BG_CREDITS : 0;
    if (toolCredits && !requireCredits(toolCredits)) {
      setIsProcessing(false);
      return;
    }
    if (action === "remove_background") {
      setProcessingMessage("Removing the background");
      setProcessingSubMessage("This uses 4 credits.");
    } else if (action === "upscale_image") {
      setProcessingMessage("Upscaling");
      setProcessingSubMessage("This is a 4K re-render, so the framing can shift. It uses 48 credits.");
    }
    setProcessingType("loading");
    try {
      const response = await fetch(currentMediaUrl);
      const blob = await response.blob();
      const file = new File([blob], "image.png", { type: "image/png" });
      // Handle other actions or continue with upload for non-upscale actions
      handleImageUpload(
        file,
        action,
        backgroundImage,
        backgroundColor,
        gradientPattern
      );
    } catch (error) {
      console.error("Error processing image:", error);
      setShowToastErrorMessage(true);
      setErrorMessage(
        "Unable to process your image. Please try again with a different image."
      );
      setErrorStatus("Processing Error");
      setIsProcessing(false);
    }
  };

  const handleErrorClose = () => {
    setShowToastErrorMessage(false);
    setErrorMessage("");
    setErrorStatus("");
  };

  const handleSuccessClose = () => {
    setShowToastSuccessMessage(false);
    setSuccessMessage("");
  };

  // Function to show status message
  const showStatusMessage = (
    message,
    subMessage,
    type = "info",
    duration = 3000
  ) => {
    setProcessingMessage(message);
    setProcessingSubMessage(subMessage);
    setProcessingType(type);
    setIsProcessing(true);
    // Auto-hide after duration if provided
    if (duration) {
      setTimeout(() => {
        setIsProcessing(false);
      }, duration);
    }
  };

  // Example usage in handleDownloadImage
  const handleDownloadImage = async () => {
    // Exit crop mode if active
    if (isCropping) {
      setIsCropping(false);
    }

    if (!currentMediaUrl) {
      setShowToastErrorMessage(true);
      setErrorMessage("Please select or create an image/video first");
      setErrorStatus("No Media Selected");
      return;
    }

    showStatusMessage(
      "Preparing Download",
      "Your media will be saved to your device shortly...",
      "info",
      1500
    );

    try {
      if (isMediaTypeVideo) {
        // Fetch the video blob to handle blob URLs or data URLs
        const response = await fetch(currentMediaUrl);
        const blob = await response.blob();

        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = `video_${Date.now()}.mp4`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);
      } else if (imageWithTransparentBackground && !isBackgroundAdded) {
        const link = document.createElement("a");
        link.href = imageWithTransparentBackground;
        link.download = "image.png";
        link.click();
      } else {
        const response = await fetch(currentMediaUrl);
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = "image.png";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);
      }
    } catch (error) {
      console.error("Error downloading media:", error);
      setShowToastErrorMessage(true);
      setErrorMessage("Failed to download file. Try again.");
      setErrorStatus("Download Error");
    }
  };

  // Add a function to handle image selection from gallery
  const handleGalleryImageSelect = (imageUrl) => {
    if (isCropping) {
      setIsCropping(false);
    }
    console.log("Gallery image selected, resetting image loaded state");
    setIsImageLoaded(false);
    setCurrentMediaUrl(imageUrl); // Use currentMediaUrl
    setIsMediaTypeVideo(false); // Gallery only provides images
    setImageWithTransparentBackground(null);
    saveMediaToHistory(imageUrl, false); // false because it's an image
  };

  // Add a function to handle comparison toggle
  const handleCompareToggle = () => {
    if (isMediaTypeVideo) {
      setShowToastErrorMessage(true);
      setErrorMessage("Comparison is not available for videos.");
      setErrorStatus("Unsupported Media");
      return;
    }
    // Exit crop mode if active
    if (isCropping) {
      setIsCropping(false);
    }
    if (addBackgroundMode) {
      setAddBackgroundMode(false);
    }
    // Only enable compare mode if we have both a current image and at least one image in the undo stack
    if (currentMediaUrl && undoStack.length > 0) {
      const lastUndoItem = undoStack[undoStack.length - 1];
      if (lastUndoItem.isVideo) {
        setShowToastErrorMessage(true);
        setErrorMessage("Cannot compare video with image or vice-versa.");
        setErrorStatus("Unsupported Comparison");
        return;
      }
      // Log image dimensions before comparison
      const prevImage = new Image();
      const currImage = new Image();
      prevImage.onload = () => {
        console.log(
          "Previous image dimensions:",
          prevImage.naturalWidth,
          "x",
          prevImage.naturalHeight
        );
      };
      currImage.onload = () => {
        console.log(
          "Current image dimensions:",
          currImage.naturalWidth,
          "x",
          currImage.naturalHeight
        );
      };
      prevImage.src = lastUndoItem.url; // Use url from history object
      currImage.src = currentMediaUrl;
      setIsCompareMode(!isCompareMode);
    } else if (!currentMediaUrl) {
      setShowToastErrorMessage(true);
      setErrorMessage("No media available to compare");
      setErrorStatus("Error");
    } else if (undoStack.length === 0) {
      setShowToastErrorMessage(true);
      setErrorMessage("No previous media available to compare");
      setErrorStatus("Error");
    }
  };

  const handleSaveToGallery = async () => {
    console.log("Save to gallery");
    if (isMediaTypeVideo) {
      setShowToastErrorMessage(true);
      setErrorMessage("Saving videos to gallery is not supported yet.");
      setErrorStatus("Unsupported Media");
      return;
    }
    // Exit crop mode if active
    if (isCropping) {
      setIsCropping(false);
    }
    try {
      if (!currentMediaUrl) {
        // Use currentMediaUrl
        setShowToastErrorMessage(true);
        setErrorMessage("No image available to save to your gallery");
        setErrorStatus("Missing Image");
        return;
      }
      // Show processing status
      setIsProcessing(true);
      setProcessingMessage("Saving to Your Gallery");
      setProcessingSubMessage("Uploading and processing your image...");
      setProcessingType("loading");
      // Convert currentMediaUrl URL to file
      const response = await fetch(currentMediaUrl);
      const blob = await response.blob();
      // Create a File object with appropriate name and type
      const fileName = `image_${Date.now()}.png`;
      const file = new File([blob], fileName, { type: "image/png" });
      const result = await uploadExperimentImage(file, accessToken, {});
      console.log("result:", result);
      // Hide processing status
      setIsProcessing(false);
      if (result.status === "success") {
        console.log("Image saved to gallery");
        setShowToastSuccessMessage(true);
        setSuccessMessage("Image successfully added to your gallery");
        // Auto-hide success message after 3 seconds
        setTimeout(() => {
          setShowToastSuccessMessage(false);
          setSuccessMessage("");
        }, 3000);
      } else {
        setShowToastErrorMessage(true);
        setErrorMessage(
          "Unable to save image to your gallery. Please try again."
        );
        setErrorStatus("Save Failed");
      }
    } catch (error) {
      console.error("Error saving to gallery:", error);
      setIsProcessing(false);
      setShowToastErrorMessage(true);
      setErrorMessage(
        "Connection issue while saving your image. Please check your internet connection and try again."
      );
      setErrorStatus("Connection Error");
    }
  };

  const handleCrop = () => {
    if (isMediaTypeVideo) {
      setShowToastErrorMessage(true);
      setErrorMessage("Cropping is not available for videos.");
      setErrorStatus("Unsupported Media");
      return;
    }
    // Disable chat mode when entering crop mode
    setShowChat(false);
    setIsCaptioning(false);
    setShowCaptionOptions(false);
    // Disable compare mode if active
    if (isCompareMode) {
      setIsCompareMode(false);
    }
    if (addBackgroundMode) {
      setAddBackgroundMode(false);
    }
    // Reset transparent background image when entering crop mode
    setImageWithTransparentBackground(null);
    if (isCropping) {
      // If already in cropping mode, exit without applying crop
      setIsCropping(false);
    } else {
      // Enter cropping mode
      setIsCropping(true);
      // If zoomed in, reset to 100% for easier cropping
      if (zoom !== 100) {
        // The useEffect for zoom will trigger initializeCropBox
        setZoom(100);
      } else {
        // If already at 100%, initialize the crop box directly
        initializeCropBox();
      }
    }
  };

  // Move initializeCropBox to be a useCallback
  const initializeCropBox = useCallback(() => {
    const canvas = cropCanvasRef.current;
    const img = imageRef.current;
    if (canvas && img && !isMediaTypeVideo) {
      // Only for images
      // Get the actual dimensions of the image as rendered
      const imgRect = img.getBoundingClientRect();
      // Set canvas dimensions to match the image
      canvas.width = imgRect.width;
      canvas.height = imgRect.height;
      // Set initial crop box to center of image with 80% dimensions
      const width = imgRect.width * 0.8;
      const height = imgRect.height * 0.8;
      const x = (imgRect.width - width) / 2;
      const y = (imgRect.height - height) / 2;
      setCropBox({ x, y, width, height });
    }
  }, [cropCanvasRef, imageRef, isMediaTypeVideo]);

  const applyCrop = () => {
    if (isMediaTypeVideo) {
      console.warn("applyCrop called with a video. Aborting.");
      setIsCropping(false);
      return;
    }
    if (!isCropping || !currentMediaUrl || !cropBox.width || !cropBox.height) {
      // Use currentMediaUrl
      setIsCropping(false);
      return;
    }
    // Create a canvas to draw the cropped image
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    const img = new Image();
    img.onload = () => {
      // Get the image element and its dimensions
      const imageElem = imageRef.current;
      if (!imageElem) {
        console.error("Image element not found");
        setIsCropping(false);
        return;
      }
      // Get the actual displayed image dimensions
      const imgRect = imageElem.getBoundingClientRect();
      // Calculate scaling ratio between displayed and natural image
      const scaleX = img.naturalWidth / imgRect.width;
      const scaleY = img.naturalHeight / imgRect.height;
      // Apply scaling to get actual crop coordinates on the natural image
      const sourceX = cropBox.x * scaleX;
      const sourceY = cropBox.y * scaleY;
      const sourceWidth = cropBox.width * scaleX;
      const sourceHeight = cropBox.height * scaleY;
      // Set canvas dimensions to the cropped size
      canvas.width = sourceWidth;
      canvas.height = sourceHeight;
      // Draw the cropped portion to the canvas
      try {
        ctx.drawImage(
          img,
          sourceX,
          sourceY,
          sourceWidth,
          sourceHeight,
          0,
          0,
          sourceWidth,
          sourceHeight
        );
        // Convert canvas to new image URL
        const croppedImageUrl = canvas.toDataURL("image/png");
        // Show success message
        showStatusMessage(
          "Image Cropped",
          "Your image has been successfully cropped",
          "info",
          1500
        );
        // Save to history and update current image
        saveMediaToHistory(croppedImageUrl, false); // It's an image
        setCurrentMediaUrl(croppedImageUrl); // Update currentMediaUrl
        // Exit cropping mode
        setIsCropping(false);
      } catch (error) {
        console.error("Error cropping image:", error);
        showStatusMessage(
          "Crop Failed",
          "Unable to crop image. Please try again.",
          "warning",
          2000
        );
        setIsCropping(false);
      }
    };
    img.onerror = () => {
      console.error("Failed to load image for cropping");
      showStatusMessage(
        "Crop Failed",
        "Unable to load image for cropping",
        "warning",
        2000
      );
      setIsCropping(false);
    };
    img.src = currentMediaUrl; // Use currentMediaUrl
  };

  const cancelCrop = () => {
    setIsCropping(false);
  };

  const handleCropMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isCropping || isProcessing || isMediaTypeVideo) return; // Only for images
    const canvas = cropCanvasRef.current;
    if (!canvas) return;
    // Get canvas coordinates
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const x = (e.clientX - rect.left) * scaleX;
    const y = (e.clientY - rect.top) * scaleY;
    // Check if clicking on a resize handle
    const handleSize = 12; // Increased handle hit area
    const isInside = (px, py, hx, hy) =>
      px >= hx - handleSize &&
      px <= hx + handleSize &&
      py >= hy - handleSize &&
      py <= hy + handleSize;
    // Check each resize handle
    if (isInside(x, y, cropBox.x, cropBox.y)) {
      setResizeHandle("nw");
    } else if (isInside(x, y, cropBox.x + cropBox.width, cropBox.y)) {
      setResizeHandle("ne");
    } else if (isInside(x, y, cropBox.x, cropBox.y + cropBox.height)) {
      setResizeHandle("sw");
    } else if (
      isInside(x, y, cropBox.x + cropBox.width, cropBox.y + cropBox.height)
    ) {
      setResizeHandle("se");
    } else if (isInside(x, y, cropBox.x + cropBox.width / 2, cropBox.y)) {
      setResizeHandle("n");
    } else if (
      isInside(x, y, cropBox.x + cropBox.width, cropBox.y + cropBox.height / 2)
    ) {
      setResizeHandle("e");
    } else if (
      isInside(x, y, cropBox.x + cropBox.width / 2, cropBox.y + cropBox.height)
    ) {
      setResizeHandle("s");
    } else if (isInside(x, y, cropBox.x, cropBox.y + cropBox.height / 2)) {
      setResizeHandle("w");
    } else if (
      x > cropBox.x &&
      x < cropBox.x + cropBox.width &&
      y > cropBox.y &&
      y < cropBox.y + cropBox.height
    ) {
      // Clicking inside crop box - drag the whole box
      setIsDraggingCrop(true);
    } else {
      // Clicking outside - start a new crop box
      const minSize = 100; // Minimum size for new crop box
      setCropBox({
        x: Math.max(0, x - minSize / 2),
        y: Math.max(0, y - minSize / 2),
        width: minSize,
        height: minSize,
      });
      setResizeHandle("se");
    }
    setCropStartPos({ x, y });
  };

  const handleCropMouseMove = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isCropping || (!isDraggingCrop && !resizeHandle) || isMediaTypeVideo)
      return; // Only for images
    const canvas = cropCanvasRef.current;
    if (!canvas) return;
    // Get canvas coordinates
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const x = (e.clientX - rect.left) * scaleX;
    const y = (e.clientY - rect.top) * scaleY;
    const dx = x - cropStartPos.x;
    const dy = y - cropStartPos.y;
    if (isDraggingCrop) {
      // Move the entire crop box
      setCropBox((prev) => ({
        ...prev,
        x: Math.max(0, Math.min(canvas.width - prev.width, prev.x + dx)),
        y: Math.max(0, Math.min(canvas.height - prev.height, prev.y + dy)),
      }));
      setCropStartPos({ x, y });
    } else if (resizeHandle) {
      const newBox = { ...cropBox };
      // Handle resizing based on which handle is being dragged
      switch (resizeHandle) {
        case "nw":
          newBox.width = Math.max(10, newBox.width - dx);
          newBox.height = Math.max(10, newBox.height - dy);
          newBox.x = Math.min(newBox.x + dx, newBox.x + newBox.width - 10);
          newBox.y = Math.min(newBox.y + dy, newBox.y + newBox.height - 10);
          break;
        case "ne":
          newBox.width = Math.max(10, newBox.width + dx);
          newBox.height = Math.max(10, newBox.height - dy);
          newBox.y = Math.min(newBox.y + dy, newBox.y + newBox.height - 10);
          break;
        case "sw":
          newBox.width = Math.max(10, newBox.width - dx);
          newBox.height = Math.max(10, newBox.height + dy);
          newBox.x = Math.min(newBox.x + dx, newBox.x + newBox.width - 10);
          break;
        case "se":
          newBox.width = Math.max(10, newBox.width + dx);
          newBox.height = Math.max(10, newBox.height + dy);
          break;
        case "n":
          newBox.height = Math.max(10, newBox.height - dy);
          newBox.y = Math.min(newBox.y + dy, newBox.y + newBox.height - 10);
          break;
        case "e":
          newBox.width = Math.max(10, newBox.width + dx);
          break;
        case "s":
          newBox.height = Math.max(10, newBox.height + dy);
          break;
        case "w":
          newBox.width = Math.max(10, newBox.width - dx);
          newBox.x = Math.min(newBox.x + dx, newBox.x + newBox.width - 10);
          break;
        default:
          break;
      }
      // Ensure crop box stays within image bounds
      newBox.x = Math.max(0, Math.min(canvas.width - 10, newBox.x));
      newBox.y = Math.max(0, Math.min(canvas.height - 10, newBox.y));
      newBox.width = Math.max(
        10,
        Math.min(canvas.width - newBox.x, newBox.width)
      );
      newBox.height = Math.max(
        10,
        Math.min(canvas.height - newBox.y, newBox.height)
      );
      setCropBox(newBox);
      setCropStartPos({ x, y });
    }
  };

  const handleCropMouseUp = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingCrop(false);
    setResizeHandle(null);
  };

  // Draw crop overlay on canvas
  useEffect(() => {
    if (
      !isCropping ||
      !cropCanvasRef.current ||
      !imageRef.current ||
      isMediaTypeVideo
    )
      return; // Only for images
    const canvas = cropCanvasRef.current;
    const ctx = canvas.getContext("2d");
    const img = imageRef.current;
    if (!ctx) return;
    // Get the actual dimensions of the image as rendered
    const imgRect = img.getBoundingClientRect();
    // Make sure canvas dimensions match the image
    if (canvas.width !== imgRect.width || canvas.height !== imgRect.height) {
      canvas.width = imgRect.width;
      canvas.height = imgRect.height;
    }
    // Clear canvas first
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    // Draw semi-transparent overlay
    ctx.fillStyle = "rgba(0, 0, 0, 0.5)";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    // Clear the crop area
    ctx.clearRect(cropBox.x, cropBox.y, cropBox.width, cropBox.height);
    // Draw crop border with shadow
    ctx.shadowColor = "rgba(0, 0, 0, 0.5)";
    ctx.shadowBlur = 4;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 0;
    ctx.strokeStyle = "white";
    ctx.lineWidth = 2;
    ctx.strokeRect(cropBox.x, cropBox.y, cropBox.width, cropBox.height);
    // Reset shadow for handles
    ctx.shadowColor = "transparent";
    ctx.shadowBlur = 0;
    // Draw resize handles
    const handleSize = 8;
    const drawHandle = (x, y) => {
      // Draw handle shadow
      ctx.beginPath();
      ctx.arc(x, y, handleSize + 1, 0, Math.PI * 2);
      ctx.fillStyle = "rgba(0, 0, 0, 0.3)";
      ctx.fill();
      // Draw handle
      ctx.beginPath();
      ctx.arc(x, y, handleSize, 0, Math.PI * 2);
      ctx.fillStyle = "white";
      ctx.fill();
      // Draw handle border
      ctx.strokeStyle = "rgba(0, 0, 0, 0.5)";
      ctx.lineWidth = 1;
      ctx.stroke();
      // Draw inner highlight
      ctx.beginPath();
      ctx.arc(x, y, handleSize - 1, 0, Math.PI * 2);
      ctx.strokeStyle = "rgba(255, 255, 255, 0.5)";
      ctx.lineWidth = 1;
      ctx.stroke();
    };
    // Corner handles
    drawHandle(cropBox.x, cropBox.y); // Top-left
    drawHandle(cropBox.x + cropBox.width, cropBox.y); // Top-right
    drawHandle(cropBox.x, cropBox.y + cropBox.height); // Bottom-left
    drawHandle(cropBox.x + cropBox.width, cropBox.y + cropBox.height); // Bottom-right
    // Edge handles
    drawHandle(cropBox.x + cropBox.width / 2, cropBox.y); // Top-center
    drawHandle(cropBox.x + cropBox.width, cropBox.y + cropBox.height / 2); // Right-center
    drawHandle(cropBox.x + cropBox.width / 2, cropBox.y + cropBox.height); // Bottom-center
    drawHandle(cropBox.x, cropBox.y + cropBox.height / 2); // Left-center
  }, [isCropping, cropBox, isMediaTypeVideo]);

  // Add an effect to update the crop canvas when the zoom changes
  useEffect(() => {
    if (isCropping && !isMediaTypeVideo) {
      // Only for images
      // Small delay to allow the zoom render to complete
      setTimeout(() => {
        initializeCropBox();
      }, 100);
    }
  }, [zoom, isCropping, initializeCropBox, isMediaTypeVideo]);

  const handleCaption = () => {
    if (isProcessing || currentMediaUrl === null || isMediaTypeVideo) {
      // Disable for video
      if (isMediaTypeVideo) {
        setShowToastErrorMessage(true);
        setErrorMessage("Captioning is not available for videos.");
        setErrorStatus("Unsupported Media");
      }
      return;
    }
    // Disable chat mode if active
    if (showChat) {
      setShowChat(false);
    }
    // Disable crop mode if active
    if (isCropping) {
      setIsCropping(false);
    }
    // Disable compare mode if active
    if (isCompareMode) {
      setIsCompareMode(false);
    }
    if (addBackgroundMode) {
      setAddBackgroundMode(false);
    }
    if (resizeImage) {
      setResizeImage(false);
    }
    setIsCaptioning(!isCaptioning);
    // Toggle caption options
    setShowCaptionOptions(!showCaptionOptions);
  };

  const handleCaptionOption = async (option) => {
    if (isMediaTypeVideo) {
      // Disable for video
      setShowToastErrorMessage(true);
      setErrorMessage("Captioning is not available for videos.");
      setErrorStatus("Unsupported Media");
      setIsProcessing(false);
      return;
    }
    if (!requireCredits(CHAT_CREDITS)) return;
    setShowCaptionOptions(false);
    setIsProcessing(true);
    setProcessingType("loading");
    setProcessingMessage("Writing a caption");
    setProcessingSubMessage("This uses 1 credit.");
    try {
      // Fetch the image data from the URL
      const imageResponse = await fetch(currentMediaUrl); // Use currentMediaUrl
      const imageBlob = await imageResponse.blob();
      // Create a File object from the blob
      const file = new File([imageBlob], "image.png", { type: "image/png" });
      const formdata = new FormData();
      formdata.append("image", file);
      formdata.append("format", option);
      const response = await fetch(`${TRYON_SERVER_URL}/api/v1/studio/chat/`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        body: formdata,
      });
      if (response.ok) {
        const data = await response.json();
        console.log("Caption:", data);
        setIsProcessing(false);
        setShowCaptionRequestResponse(true);
        // Format the response based on the option
        if (option === "json") {
          try {
            // If it's already an object, stringify it with proper formatting
            const formattedJson =
              typeof data.caption === "object"
                ? JSON.stringify(data.caption, null, 2)
                : JSON.stringify(JSON.parse(data.caption), null, 2);
            setCaptionRequestResponse(formattedJson);
          } catch (e) {
            // If parsing fails, show the raw response
            setCaptionRequestResponse(data.caption);
          }
        } else {
          setCaptionRequestResponse(data.caption);
        }
        await refreshCredits();
      } else {
        const payload = await response.json().catch(() => null);
        setIsProcessing(false);
        setShowCaptionRequestResponse(true);
        setCaptionRequestResponse(payload?.message || payload?.error || "The caption could not be written.");
      }
    } catch (error) {
      console.error("Error:", error);
      setIsProcessing(false);
      setShowCaptionRequestResponse(true);
      setCaptionRequestResponse(
        "An error occurred while generating the caption."
      );
    }
  };

  const handleChatSubmit = async () => {
    if (!chatInput.trim() || isChatProcessing || isMediaTypeVideo) {
      // Disable for video
      if (isMediaTypeVideo) {
        setShowToastErrorMessage(true);
        setErrorMessage("Chat is not available for videos.");
        setErrorStatus("Unsupported Media");
      }
      return;
    }
    if (!requireCredits(CHAT_CREDITS)) return;
    setIsChatProcessing(true);
    const currentQuery = chatInput;
    setChatInput("");
    // Add the user's message and a processing state
    setChatResponses((prev) => [
      ...prev,
      {
        query: currentQuery,
        response: "processing",
      },
    ]);
    try {
      const imageResponse = await fetch(currentMediaUrl); // Use currentMediaUrl
      const imageBlob = await imageResponse.blob();
      const file = new File([imageBlob], "image.png", { type: "image/png" });
      const formdata = new FormData();
      formdata.append("image", file);
      formdata.append("prompt", currentQuery);
      const response = await fetch(`${TRYON_SERVER_URL}/api/v1/studio/chat/`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        body: formdata,
      });
      if (response.ok) {
        const data = await response.json();
        // Update the last response with the actual response
        setChatResponses((prev) => {
          const newResponses = [...prev];
          newResponses[newResponses.length - 1] = {
            query: currentQuery,
            response: data.caption || "No response received",
          };
          return newResponses;
        });
        await refreshCredits();
      } else {
        const payload = await response.json().catch(() => null);
        setChatResponses((prev) => {
          const newResponses = [...prev];
          newResponses[newResponses.length - 1] = {
            query: currentQuery,
            response: payload?.message || payload?.error || "The reply could not be written.",
          };
          return newResponses;
        });
      }
    } catch (error) {
      console.error("Error:", error);
      // Update the last response with an error message
      setChatResponses((prev) => {
        const newResponses = [...prev];
        newResponses[newResponses.length - 1] = {
          query: currentQuery,
          response:
            "Sorry, I encountered an error while processing your request. Please try again.",
        };
        return newResponses;
      });
    } finally {
      setIsChatProcessing(false);
    }
  };

  const handleGenerateEditImage = async () => {
    if (!currentSettings.prompt.trim() || isProcessing || isMediaTypeVideo) {
      if (isMediaTypeVideo) {
        setShowToastErrorMessage(true);
        setErrorMessage(
          "Image generation/editing is not available for videos."
        );
        setErrorStatus("Unsupported Media");
      }
      return;
    }

    if (!requireCredits(editCredits)) return;

    setIsProcessing(true);
    setProcessingType("loading");
    setProcessingMessage("Editing the image");
    setProcessingSubMessage(`This uses ${editCredits} credits.`);

    try {
      let imageFile = null;
      if (currentMediaUrl) {
        const imageResponse = await fetch(currentMediaUrl);
        const blob = await imageResponse.blob();
        imageFile = new File([blob], "image.png", { type: blob.type || "image/png" });
      }

      const result = await generateImage({
        prompt: currentSettings.prompt,
        resolution: "2K",
        n: 1,
        images: imageFile ? [imageFile] : [],
        accessToken,
      });

      if (result.status !== "success" || !result.data?.download_url) {
        throw new Error(result.message || "The edit failed");
      }

      const secureImageUrl = await loadGeneratedImage(result.data.download_url, accessToken);
      setCurrentMediaUrl(secureImageUrl);
      saveMediaToHistory(secureImageUrl, false);
      await refreshCredits();

      setShowToastSuccessMessage(true);
      setSuccessMessage("Image edited");

      setTimeout(() => {
        setShowToastSuccessMessage(false);
        setSuccessMessage("");
      }, 3000);
    } catch (error) {
      console.error("Error:", error);
      setShowToastErrorMessage(true);
      setErrorMessage(error.message || "The edit failed. Try again.");
      setErrorStatus("Processing Error");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleGenerateVideo = async () => {
    console.log("Starting video generation with settings:", currentSettings);

    // Prevent multiple simultaneous generations
    if (isProcessing) {
      console.warn("Video generation already in progress");
      return;
    }

    // Set initial processing state
    setIsProcessing(true);
    setProcessingType("loading");
    setProcessingMessage("Starting video generation...");
    setProcessingSubMessage("Initializing...");

    try {
      // Create video generation service instance
      const videoService = createVideoGenerationService(accessToken);

      // Prepare settings
      const settings = {
        prompt: currentSettings?.prompt,
        aspectRatio: currentSettings?.aspect_ratio || "16:9",
        resolution: currentSettings?.resolution || "1080p",
        duration: currentSettings?.duration || "8",
      };
      const clipCredits = videoCredits(settings.duration, settings.resolution);
      const balance = Number(userData?.credits);
      if (!Number.isFinite(balance) || balance < clipCredits) {
        setIsProcessing(false);
        navigate(`/credits?buy_credits=true&num_of_credits=${clipCredits}`);
        return;
      }

      // Generate video with progress updates
      const result = await videoService.generateVideo(settings, (progress) => {
        // Update UI based on progress
        setProcessingMessage(progress.message);
        setProcessingSubMessage(progress.subMessage);

        // You can add a progress bar here if needed
        if (progress.progress !== undefined) {
          console.log(`Progress: ${progress.progress}%`);
        }
      });

      // Handle result
      if (result.status === "success") {
        const videoUrl = result.data.videoUrl;

        // Update UI with generated video
        setCurrentMediaUrl(videoUrl);
        saveMediaToHistory(videoUrl, true);
        const user = await fetchUser(accessToken);
        if (user.status === "success") {
          dispatch(setUserData(JSON.stringify(user.data)));
        }

        // Show success message
        setShowToastSuccessMessage(true);
        setSuccessMessage(result.message);

        // Auto-hide success message after 3 seconds
        setTimeout(() => {
          setShowToastSuccessMessage(false);
          setSuccessMessage("");
        }, 3000);
      } else {
        // Handle error from video service
        throw new Error(result.message || "Video generation failed");
      }
    } catch (error) {
      console.error("Video generation error:", error);

      // Show error message to user
      setShowToastErrorMessage(true);
      setErrorMessage(
        error.message || "Error generating video. Please try again."
      );
      setErrorStatus("Processing Error");

      // Auto-hide error message after 5 seconds
      setTimeout(() => {
        setShowToastErrorMessage(false);
        setErrorMessage("");
        setErrorStatus("");
      }, 5000);
    } finally {
      // Always reset processing state
      setIsProcessing(false);
      setProcessingType("loading");
      setProcessingMessage("Processing");
      setProcessingSubMessage("");
    }
  };

  // Processing Status Component
  const ProcessingStatus = () => {
    return (
      <div className="fixed inset-0 z-30 flex items-center justify-center bg-black/40 p-4">
        <div className="mx-auto flex w-full max-w-sm flex-col items-center justify-center rounded-2xl bg-white px-8 py-6 shadow-[0_0_0_1px_var(--hairline)]">
          <div className="text-center space-y-4">
            {processingType === "loading" && (
              <div className="flex items-center justify-center">
                <div className="h-10 w-10 animate-spin rounded-full border-2 border-primary-200 border-t-primary-700"></div>
              </div>
            )}
            {processingType === "info" && (
              <div className="flex items-center justify-center">
                <div className="w-12 h-12 flex items-center justify-center bg-blue-100 text-blue-500 rounded-full">
                  <InfoIcon className="w-8 h-8" />
                </div>
              </div>
            )}
            {processingType === "warning" && (
              <div className="flex items-center justify-center">
                <div className="w-12 h-12 flex items-center justify-center bg-amber-100 text-amber-500 rounded-full">
                  <svg
                    className="w-8 h-8"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fillRule="evenodd"
                      d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    ></path>
                  </svg>
                </div>
              </div>
            )}
            <div className="mt-4">
              <p className="text-gray-700 font-semibold text-lg">
                {processingMessage}
              </p>
              <p className="text-gray-500 text-sm mt-2">
                {processingSubMessage}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Update the chat button click handler
  const handleChatToggle = () => {
    if (isProcessing || currentMediaUrl === null || isMediaTypeVideo) {
      // Disable for video
      if (isMediaTypeVideo) {
        setShowToastErrorMessage(true);
        setErrorMessage("Chat is not available for videos.");
        setErrorStatus("Unsupported Media");
      }
      return;
    }
    if (addBackgroundMode) {
      setAddBackgroundMode(false);
    }
    if (resizeImage) {
      setResizeImage(false);
    }
    // If chat is being enabled, disable crop mode
    if (!showChat) {
      if (isCropping) {
        setIsCropping(false);
      }
      if (isCaptioning) {
        setIsCaptioning(false);
        setShowCaptionOptions(false);
      }
    }
    setShowChat(!showChat);
  };

  // Add effect to handle auto-scrolling in chat
  useEffect(() => {
    if (chatContainerRef.current && chatResponses.length > 0) {
      const container = chatContainerRef.current;
      container.scrollTop = container.scrollHeight;
    }
  }, [chatResponses]);

  // Update the caption response display
  const CaptionResponse = () => {
    if (!showCaptionRequestResponse) return null;
    const isJson =
      captionRequestResponse.trim().startsWith("{") ||
      captionRequestResponse.trim().startsWith("[");
    const handleCopy = async () => {
      try {
        await navigator.clipboard.writeText(captionRequestResponse);
        setShowCopyToast(true);
        // Auto hide toast after 2 seconds
        setTimeout(() => setShowCopyToast(false), 2000);
      } catch (err) {
        console.error("Failed to copy text: ", err);
      }
    };
    return (
      <>
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="max-w-2xl w-full mx-4 bg-white rounded-lg shadow-xl border border-gray-200">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">
                Generated Caption
              </h3>
              <button
                onClick={() => setShowCaptionRequestResponse(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
            <div className="p-4 h-[60vh] overflow-y-auto">
              {isJson ? (
                <pre className="bg-gray-50 rounded-lg p-4 overflow-x-auto text-sm">
                  <code className="text-gray-800">
                    {captionRequestResponse}
                  </code>
                </pre>
              ) : (
                <div className="text-gray-700 whitespace-pre-wrap">
                  {captionRequestResponse}
                </div>
              )}
            </div>
            <div className="flex justify-end p-4 border-t border-gray-200">
              <button
                onClick={handleCopy}
                className="rounded-full bg-primary-700 px-4 py-2 font-Mulish text-sm font-semibold text-white"
              >
                Copy to Clipboard
              </button>
            </div>
          </div>
        </div>
        {/* Copy Toast */}
        {showCopyToast && (
          <div className="fixed bottom-4 left-4 z-50 animate-fade-in-up">
            <div className="flex items-center p-4 mb-4 text-gray-500 bg-white rounded-lg shadow-lg border border-gray-200">
              <div className="inline-flex items-center justify-center flex-shrink-0 w-8 h-8 text-green-500 bg-green-100 rounded-lg">
                <svg
                  className="w-5 h-5"
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5Zm3.707 8.207-4 4a1 1 0 0 1-1.414 0l-2-2a1 1 0 0 1 1.414-1.414L9 10.586l3.293-3.293a1 1 0 0 1 1.414 1.414Z" />
                </svg>
              </div>
              <div className="ms-3 text-sm font-normal">
                Text copied to clipboard
              </div>
            </div>
          </div>
        )}
      </>
    );
  };

  // Add click outside handler for settings dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (settingsRef.current && !settingsRef.current.contains(event.target)) {
        setShowSettings(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleModeSelection = (mode) => {
    setSelectedMode(mode);
    if (mode === "image") {
      setCurrentSettings(imageSettings);
    } else if (mode === "video") {
      setCurrentSettings(videoSettings);
    }
  };

  return (
    <div
      className="flex flex-col bg-ground"
      style={{ height: containerHeight }}
    >
      <div
        className="flex overflow-auto relative"
        style={{ height: containerHeight }}
      >
        {authenticated === null ? (
          <div className="flex w-full h-full">
            {/* Main content area skeleton */}
            <div className="flex-1 m-1">
              <div className="w-full h-full flex items-center justify-center bg-white/80 shadow-md rounded-lg animate-pulse">
                <Spinner />
              </div>
            </div>
            {/* Tools sidebar skeleton */}
            <div className="w-14 md:w-16 lg:w-18 ml-1 p-1">
              <div className="h-full bg-white/80 rounded-lg shadow-md animate-pulse">
                <div className="h-10 bg-gradient-to-b from-gray-800 to-gray-900 rounded-t-lg"></div>
                <div className="p-2 space-y-2">
                  {[...Array(7)].map((_, i) => (
                    <div
                      key={i}
                      className="flex flex-col items-center p-3 border-b border-slate-100"
                    >
                      <div className="w-8 h-8 bg-slate-100 rounded-full"></div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ) : authenticated ? (
          <>
            <div className="size-20 grow" style={{ height: containerHeight }}>
              {!currentMediaUrl ? ( // Use currentMediaUrl
                <div
                  className="flex flex-col items-center justify-start pt-12"
                  style={{ height: containerHeight }}
                >
                  <div className="mb-8 flex flex-col items-center justify-center text-center">
                    <h1 className="font-Mulish font-semibold text-4xl text-secondary">Studio</h1>
                    <p className="mt-2 max-w-md font-Mulish text-sm text-graphite">
                      Remove a background, upscale, edit, or caption a photo.
                    </p>
                  </div>
                  <div className="w-full max-w-lg">
                    <label
                      htmlFor="dropzone-file"
                      className={`flex cursor-pointer flex-col items-center justify-center rounded-2xl border border-dashed border-hairline-strong bg-white ${
                        isDragging ? "bg-primary-50" : "hover:bg-primary-50"
                      }`}
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}
                    >
                      <div className="flex flex-col items-center justify-center pt-8 pb-8 px-6">
                        <CloudUploadIcon className="mb-3 h-10 w-10 text-primary-700" />
                        <p className="font-Mulish font-semibold text-lg text-secondary">Upload a photo</p>
                        <p className="mt-1 font-Mulish text-sm text-graphite">Or drop it here. PNG, JPG, or a short video.</p>
                      </div>
                      <input
                        id="dropzone-file"
                        type="file"
                        className="hidden"
                        onChange={handleFileSelected}
                      />
                    </label>
                    <button
                      type="button"
                      onClick={() => setShowGalleryModal(true)}
                      className="mx-auto mt-4 flex items-center justify-center rounded-full border border-primary-200 bg-white px-4 py-2.5 font-Mulish text-sm text-primary-700 hover:bg-primary-50"
                    >
                      <ImageIcon className="mr-2 h-4 w-4" />
                      Select from gallery
                    </button>
                  </div>
                </div>
              ) : (
                <div
                  className="w-full relative"
                  style={{ height: containerHeight }}
                >
                  <div
                    ref={containerRef}
                    className={`w-full h-full overflow-auto ${
                      zoom > 100 && !isCropping && !isMediaTypeVideo
                        ? "cursor-grab"
                        : "" // Only grab cursor for images when zoomed
                    }`}
                    onMouseDown={
                      zoom > 100 && !isCropping && !isMediaTypeVideo
                        ? handleMouseDown
                        : undefined
                    }
                    onMouseMove={
                      zoom > 100 && !isCropping && !isMediaTypeVideo
                        ? handleMouseMove
                        : undefined
                    }
                    onMouseUp={handleMouseUp}
                    onMouseLeave={handleMouseUp}
                    style={{
                      position: "relative",
                      overflow: "auto",
                      scrollbarWidth: "thin",
                      scrollbarColor: "rgba(0, 0, 0, 0.2) transparent",
                    }}
                  >
                    <div
                      style={{
                        padding:
                          zoom > 100 && !isMediaTypeVideo // Only apply padding for images when zoomed
                            ? `${Math.max(zoom * 1.9, 150)}px`
                            : `${20 + extraPadding}px`,
                        minHeight: "100%",
                        minWidth: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        position: "relative",
                      }}
                    >
                      <div
                        className="image-container"
                        style={{
                          transform: isMediaTypeVideo
                            ? "none"
                            : `scale(${zoom / 100})`, // No zoom for video
                          transformOrigin: "center",
                          transition: "transform 0.2s ease-out",
                          maxHeight: "100%",
                          maxWidth: "100%",
                          position: "relative",
                        }}
                      >
                        {isCompareMode &&
                        undoStack.length > 0 &&
                        !isMediaTypeVideo ? ( // Only compare for images
                          <div className="relative w-full h-full flex items-center justify-center">
                            <div className="w-full h-full flex items-center justify-center">
                              <ReactCompareSlider
                                key={`${
                                  undoStack[undoStack.length - 1].url // Use .url
                                }-${currentMediaUrl}`}
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  maxWidth: "100%",
                                  maxHeight: `100%`,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                                position={50}
                                itemOne={
                                  <div className="w-full h-full flex items-center justify-center overflow-hidden">
                                    <ReactCompareSliderImage
                                      src={undoStack[undoStack.length - 1].url} // Use .url
                                      srcSet={
                                        undoStack[undoStack.length - 1].url
                                      } // Use .url
                                      alt="Previous image"
                                      style={{
                                        width: "100%",
                                        height: "100%",
                                        objectFit: "contain",
                                        objectPosition: "center",
                                      }}
                                    />
                                  </div>
                                }
                                itemTwo={
                                  <div className="w-full h-full flex items-center justify-center overflow-hidden">
                                    <ReactCompareSliderImage
                                      src={currentMediaUrl}
                                      srcSet={currentMediaUrl}
                                      alt="Current image"
                                      style={{
                                        width: "100%",
                                        height: "100%",
                                        objectFit: "contain",
                                        objectPosition: "center",
                                      }}
                                    />
                                  </div>
                                }
                              />
                            </div>
                          </div>
                        ) : (
                          <div
                            className="relative flex items-center justify-center"
                            ref={cropContainerRef}
                          >
                            <div className="relative">
                              {isMediaTypeVideo ? (
                                <video
                                  ref={imageRef}
                                  src={currentMediaUrl}
                                  controls
                                  autoPlay
                                  loop
                                  className="max-w-full max-h-full object-contain"
                                  style={{
                                    display: "block",
                                    maxHeight: "calc(100vh - 100px)",
                                    maxWidth: "calc(100vw - 150px)",
                                    margin: "auto",
                                  }}
                                  onLoadedMetadata={(e) => {
                                    if (containerRef.current) {
                                      const container = containerRef.current;
                                      // For video
                                      setTimeout(() => {
                                        container.scrollLeft =
                                          (container.scrollWidth -
                                            container.clientWidth) /
                                          2;
                                        container.scrollTop =
                                          (container.scrollHeight -
                                            container.clientHeight) /
                                          2;
                                      }, 50);
                                    }
                                  }}
                                />
                              ) : (
                                <img
                                  ref={imageRef}
                                  src={currentMediaUrl || "/placeholder.svg"}
                                  alt="Current"
                                  className="max-w-full max-h-full object-contain"
                                  draggable={false}
                                  style={{
                                    display: "block",
                                    maxHeight: "calc(100vh - 100px)",
                                    maxWidth: "calc(100vw - 150px)",
                                    objectFit: "contain",
                                    margin: "auto",
                                  }}
                                  onLoad={(e) => {
                                    // Force refresh of container dimensions on image load
                                    if (containerRef.current) {
                                      const container = containerRef.current;
                                      const img = e.target;
                                      // Calculate the scale to fit the image within viewport
                                      const scaleX =
                                        (container.clientWidth - 40) /
                                        img.naturalWidth;
                                      const scaleY =
                                        (container.clientHeight - 40) /
                                        img.naturalHeight;
                                      const scale = Math.min(scaleX, scaleY, 1);
                                      // If image is larger than viewport, set initial zoom to fit
                                      if (scale < 1) {
                                        setZoom(Math.floor(scale * 100));
                                      }
                                      // Center the image
                                      setTimeout(() => {
                                        container.scrollLeft =
                                          (container.scrollWidth -
                                            container.clientWidth) /
                                          2;
                                        container.scrollTop =
                                          (container.scrollHeight -
                                            container.clientHeight) /
                                          2;
                                      }, 50);
                                    }
                                  }}
                                />
                              )}
                              {isCropping && !isMediaTypeVideo && (
                                <>
                                  <div
                                    className="absolute inset-0"
                                    style={{
                                      position: "absolute",
                                      width: "100%",
                                      height: "100%",
                                      zIndex: 10,
                                    }}
                                  >
                                    <canvas
                                      ref={cropCanvasRef}
                                      className="absolute inset-0 w-full h-full cursor-crosshair"
                                      onMouseDown={handleCropMouseDown}
                                      onMouseMove={handleCropMouseMove}
                                      onMouseUp={handleCropMouseUp}
                                      onMouseLeave={handleCropMouseUp}
                                      style={{
                                        touchAction: "none",
                                        userSelect: "none",
                                        WebkitUserSelect: "none",
                                        MozUserSelect: "none",
                                        msUserSelect: "none",
                                        pointerEvents: "auto",
                                      }}
                                    />
                                  </div>
                                  <div className="absolute top-2 right-2 flex items-center gap-2 bg-white/90 backdrop-blur-sm rounded-lg px-2 py-1 shadow-lg z-20">
                                    <button
                                      onClick={applyCrop}
                                      className="w-6 h-6 flex items-center justify-center text-white bg-green-500 hover:bg-green-600 rounded-md shadow-sm transition-colors group"
                                      title="Apply Crop"
                                    >
                                      <CheckCheckIcon className="w-3.5 h-3.5 group-hover:scale-110 transition-transform" />
                                    </button>
                                    <button
                                      onClick={cancelCrop}
                                      className="w-6 h-6 flex items-center justify-center text-white bg-red-500 hover:bg-red-600 rounded-md shadow-sm transition-colors group"
                                      title="Cancel Crop"
                                    >
                                      <CircleXIcon className="w-3.5 h-3.5 group-hover:scale-110 transition-transform" />
                                    </button>
                                  </div>
                                </>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {/* Zoom controls - only show when there is an image */}
              {currentMediaUrl && !isMediaTypeVideo && (
                <div className="absolute bottom-4 left-4 flex items-center gap-2 bg-white/95 rounded-lg px-3 py-2 shadow-md z-10">
                  <button
                    onClick={() => handleZoomChange(zoom - 10)}
                    className="w-7 h-7 flex items-center justify-center text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-full"
                    disabled={zoom <= 50}
                  >
                    -
                  </button>
                  <div className="relative w-24 h-7 flex items-center">
                    <input
                      type="range"
                      min="50"
                      max="150"
                      step="10"
                      value={zoom}
                      onChange={(e) => handleZoomChange(Number(e.target.value))}
                      className="w-full h-1.5 bg-gray-200 rounded-full appearance-none"
                      style={{
                        margin: 0,
                      }}
                    />
                  </div>
                  <button
                    onClick={() => handleZoomChange(zoom + 10)}
                    className="w-7 h-7 flex items-center justify-center text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-full"
                    disabled={zoom >= 150}
                  >
                    +
                  </button>
                  <span className="text-xs font-medium text-gray-600 min-w-[2.5rem] text-center">
                    {zoom}%
                  </span>
                </div>
              )}
              <PromptContainer
                isProcessing={isProcessing}
                handleGenerateEditImage={handleGenerateEditImage}
                handleGenerateVideo={handleGenerateVideo}
                generationSettings={currentSettings}
                onSettingsChange={setCurrentSettings}
                selectedPrompt={selectedPrompt}
                onSelectPrompt={onSelectPrompt}
                setShowPromptGalleryModal={setShowPromptGalleryModal}
                showPromptGalleryModal={showPromptGalleryModal}
                disabled={isMediaTypeVideo} // Disable input container for videos
                selectedMode={selectedMode}
                onModeChange={handleModeSelection}
              />
            </div>
            <div
              className="absolute top-0 right-0 p-1 h-full"
              style={{ height: containerHeight }}
            >
              <div className="h-full flex justify-center gap-4">
                {currentMediaUrl &&
                  showChat && ( // Only show chat if media exists
                    <ChatInterface
                      showChat={showChat}
                      setShowChat={setShowChat}
                      chatInput={chatInput}
                      setChatInput={setChatInput}
                      chatResponses={chatResponses}
                      isChatProcessing={isChatProcessing}
                      handleChatSubmit={handleChatSubmit}
                    />
                  )}
                {/* Side Background Drawer */}
                {addBackgroundMode && (
                  <ChangeBackground
                    addBackground={addBackground}
                    setCurrentImage={setCurrentMediaUrl} // Pass setCurrentMediaUrl
                    imageWithTransparentBackground={
                      imageWithTransparentBackground
                    }
                    setAddBackgroundMode={setAddBackgroundMode}
                    handleTool={handleTool}
                  />
                )}
                <div className="mr-4 flex flex-col">
                  <div className="rounded-t-2xl bg-white px-3 py-2 text-center font-Mulish text-sm font-semibold text-secondary shadow-[0_0_0_1px_var(--hairline)]">
                    Tools
                  </div>
                  <div className="flex flex-col divide-y divide-hairline rounded-b-2xl bg-white shadow-[0_0_0_1px_var(--hairline)]">
                    <button
                      onClick={() =>
                        handleTool("remove_background", null, null, true)
                      }
                      disabled={
                        isProcessing ||
                        currentMediaUrl === null ||
                        isMediaTypeVideo
                      } // Disable for video
                      className="px-2 py-3 text-sm font-medium font-Mulish text-sm text-graphite hover:bg-primary-50 hover:text-primary-700 bg-white disabled:opacity-40 disabled:hover:bg-white disabled:hover:text-graphite group relative"
                      title="Remove Background"
                    >
                      <div className="flex flex-col items-center justify-center">
                        <WandSparklesIcon className="h-6 w-6 group-hover:scale-110 transition-transform" />
                      </div>
                      <div className="absolute right-full mr-2 top-1/2 -translate-y-1/2 rounded-lg bg-white px-2.5 py-1 font-Mulish text-xs text-secondary shadow-[0_0_0_1px_var(--hairline)] opacity-0 group-hover:opacity-100 whitespace-nowrap pointer-events-none z-50">
                        Remove background (4 credits)
                        <div className="hidden"></div>
                      </div>
                    </button>
                    {/* Add Background Button */}
                    <div className="relative">
                      <button
                        onClick={() => {
                          setAddBackgroundMode(!addBackgroundMode);
                          setResizeImage(false);
                          setIsCropping(false);
                          setIsCompareMode(false);
                          setIsCaptioning(false);
                          setShowCaptionOptions(false);
                          setShowChat(false);
                          setChatInput("");
                          setChatResponses([]);
                        }}
                        disabled={
                          !currentMediaUrl || isProcessing || isMediaTypeVideo
                        } // Disable for video
                        className={`w-full px-2 py-3 text-sm font-medium text-gray-600 hover:text-red-600 bg-white hover:bg-red-50/50 transition-all duration-200 disabled:opacity-40 disabled:hover:bg-white disabled:hover:text-gray-600 group relative ${
                          addBackgroundMode
                            ? "bg-primary-50 text-primary-700"
                            : ""
                        }`}
                        title="Add Background"
                      >
                        <div className="flex flex-col items-center justify-center">
                          <LayersIcon
                            className={`h-6 w-6 group-hover:scale-110 transition-transform ${
                              addBackgroundMode ? "text-primary-700" : ""
                            }`}
                          />
                        </div>
                        {!addBackgroundMode && (
                          <div className="absolute right-full mr-2 top-1/2 -translate-y-1/2 rounded-lg bg-white px-2.5 py-1 font-Mulish text-xs text-secondary shadow-[0_0_0_1px_var(--hairline)] opacity-0 group-hover:opacity-100 whitespace-nowrap pointer-events-none z-50">
                            Change background
                            <div className="hidden"></div>
                          </div>
                        )}
                      </button>
                    </div>
                    <button
                      onClick={() =>
                        handleTool("upscale_image", null, null, false)
                      }
                      disabled={
                        isProcessing ||
                        currentMediaUrl === null ||
                        isMediaTypeVideo
                      } // Disable for video
                      className="px-2 py-3 text-sm font-medium font-Mulish text-sm text-graphite hover:bg-primary-50 hover:text-primary-700 bg-white disabled:opacity-40 disabled:hover:bg-white disabled:hover:text-graphite group relative"
                      title="Upscale Image"
                    >
                      <div className="flex flex-col items-center justify-center">
                        <ScalingIcon className="h-6 w-6 group-hover:scale-110 transition-transform" />
                      </div>
                      <div className="absolute right-full mr-2 top-1/2 -translate-y-1/2 rounded-lg bg-white px-2.5 py-1 font-Mulish text-xs text-secondary shadow-[0_0_0_1px_var(--hairline)] opacity-0 group-hover:opacity-100 whitespace-nowrap pointer-events-none z-50">
                        Upscale (48 credits)
                        <div className="hidden"></div>
                      </div>
                    </button>
                    {/* resize */}
                    <div className="relative">
                      <button
                        onClick={() => {
                          setResizeImage(!resizeImage);
                          setAddBackgroundMode(false);
                          setIsCropping(false);
                          setIsCompareMode(false);
                          setIsCaptioning(false);
                          setShowCaptionOptions(false);
                          setShowChat(false);
                          setChatInput("");
                          setChatResponses([]);
                        }}
                        disabled={
                          !currentMediaUrl || isProcessing || isMediaTypeVideo
                        } // Disable for video
                        className={`w-full px-2 py-3 text-sm font-medium text-gray-600 hover:text-red-600 bg-white hover:bg-red-50/50 transition-all duration-200 disabled:opacity-40 disabled:hover:bg-white disabled:hover:text-gray-600 group relative ${
                          resizeImage
                            ? "bg-primary-50 text-primary-700"
                            : ""
                        }`}
                        title="Resize"
                      >
                        <div className="flex flex-col items-center justify-center">
                          <ScalingIcon
                            className={`h-6 w-6 group-hover:scale-110 transition-transform ${
                              resizeImage ? "text-primary-700" : ""
                            }`}
                          />
                        </div>
                        {!resizeImage && (
                          <div className="absolute right-full mr-2 top-1/2 -translate-y-1/2 rounded-lg bg-white px-2.5 py-1 font-Mulish text-xs text-secondary shadow-[0_0_0_1px_var(--hairline)] opacity-0 group-hover:opacity-100 whitespace-nowrap pointer-events-none z-50">
                            Resize (free)
                            <div className="hidden"></div>
                          </div>
                        )}
                      </button>
                      {resizeImage && (
                        <div
                          className="absolute right-full top-1/2 -translate-y-1/4 mr-2 bg-white rounded-lg shadow-xl border border-gray-200/80"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Resize
                            setResizeImage={setResizeImage}
                            currentImage={currentMediaUrl} // Pass currentMediaUrl
                            setCurrentImage={setCurrentMediaUrl} // Pass setCurrentMediaUrl
                            saveImageToHistory={(url) =>
                              saveMediaToHistory(url, false)
                            } // Ensure it saves as image
                            setImageWithTransparentBackground={
                              setImageWithTransparentBackground
                            }
                          />
                        </div>
                      )}
                    </div>
                    <button
                      onClick={handleCrop}
                      className={`px-2 py-3 text-sm font-medium font-Mulish text-sm text-graphite hover:bg-primary-50 hover:text-primary-700 bg-white disabled:opacity-40 disabled:hover:bg-white disabled:hover:text-graphite group relative ${
                        isCropping
                          ? "bg-primary-50 text-primary-700"
                          : ""
                      }`}
                      title="Crop Image"
                      disabled={
                        isProcessing ||
                        currentMediaUrl === null ||
                        isMediaTypeVideo
                      } // Disable for video
                    >
                      <div className="flex flex-col items-center justify-center">
                        <CropIcon
                          className={`h-6 w-6 group-hover:scale-110 transition-transform ${
                            isCropping ? "text-primary-700" : ""
                          }`}
                        />
                      </div>
                      <div className="absolute right-full mr-2 top-1/2 -translate-y-1/2 rounded-lg bg-white px-2.5 py-1 font-Mulish text-xs text-secondary shadow-[0_0_0_1px_var(--hairline)] opacity-0 group-hover:opacity-100 whitespace-nowrap pointer-events-none z-50">
                        Crop (free)
                        <div className="hidden"></div>
                      </div>
                    </button>
                    <div className="relative">
                      <button
                        onClick={handleCaption}
                        className={`w-full px-2 py-3 text-sm font-medium font-Mulish text-sm text-graphite hover:bg-primary-50 hover:text-primary-700 bg-white disabled:opacity-40 disabled:hover:bg-white disabled:hover:text-graphite group relative ${
                          isCaptioning
                            ? "bg-primary-50 text-primary-700"
                            : ""
                        }`}
                        title="Caption Image"
                        disabled={
                          isProcessing ||
                          currentMediaUrl === null ||
                          isMediaTypeVideo
                        } // Disable for video
                      >
                        <div className="flex flex-col items-center justify-center">
                          <CaptionsIcon className="h-6 w-6 group-hover:scale-110 transition-transform" />
                        </div>
                        {!showCaptionOptions && (
                          <div className="absolute right-full mr-2 top-1/2 -translate-y-1/2 rounded-lg bg-white px-2.5 py-1 font-Mulish text-xs text-secondary shadow-[0_0_0_1px_var(--hairline)] opacity-0 group-hover:opacity-100 whitespace-nowrap pointer-events-none z-50">
                            Caption (1 credit)
                            <div className="hidden"></div>
                          </div>
                        )}
                        {showCaptionOptions && (
                          <div className="absolute right-full mr-2 top-1/2 -translate-y-1/2 z-50 overflow-hidden rounded-xl bg-white shadow-[0_0_0_1px_var(--hairline)]">
                            <button
                              onClick={() => handleCaptionOption("json")}
                              className="w-full border-b border-hairline px-4 py-2 font-Mulish text-sm text-graphite hover:bg-primary-50 hover:text-primary-700"
                            >
                              <div className="flex flex-col items-center justify-center">
                                <CodeIcon className="h-4 w-4  group-hover:scale-110 transition-transform" />
                                <span className="text-xs mt-1">JSON</span>
                              </div>
                            </button>
                            <button
                              onClick={() => handleCaptionOption("text")}
                              className="w-full px-4 py-2 font-Mulish text-sm text-graphite hover:bg-primary-50 hover:text-primary-700"
                            >
                              <div className="flex flex-col items-center justify-center">
                                <TypeIcon className="h-4 w-4 group-hover:scale-110 transition-transform" />
                                <span className="text-xs mt-1">Text</span>
                              </div>
                            </button>
                          </div>
                        )}
                      </button>
                      {showCaptionRequestResponse && (
                        <div className="absolute right-full mr-2 top-1/2 -translate-y-1/2 rounded-lg bg-white px-2.5 py-1 font-Mulish text-xs text-secondary shadow-[0_0_0_1px_var(--hairline)] opacity-0 group-hover:opacity-100 whitespace-nowrap pointer-events-none z-50">
                          {captionRequestResponse}
                        </div>
                      )}
                    </div>
                    <button
                      onClick={handleChatToggle}
                      className={`px-2 py-3 text-sm font-medium font-Mulish text-sm text-graphite hover:bg-primary-50 hover:text-primary-700 bg-white disabled:opacity-40 disabled:hover:bg-white disabled:hover:text-graphite group relative ${
                        showChat
                          ? "bg-primary-50 text-primary-700"
                          : ""
                      }`}
                      title="Chat"
                      disabled={
                        isProcessing ||
                        currentMediaUrl === null ||
                        isMediaTypeVideo
                      } // Disable for video
                    >
                      <div className="flex flex-col items-center justify-center">
                        <MessagesSquareIcon
                          className={`h-6 w-6 group-hover:scale-110 transition-transform ${
                            showChat ? "text-primary-700" : ""
                          }`}
                        />
                      </div>
                      {!showChat && (
                        <div className="absolute right-full mr-2 top-1/2 -translate-y-1/2 rounded-lg bg-white px-2.5 py-1 font-Mulish text-xs text-secondary shadow-[0_0_0_1px_var(--hairline)] opacity-0 group-hover:opacity-100 whitespace-nowrap pointer-events-none z-50">
                          Chat (1 credit)
                          <div className="hidden"></div>
                        </div>
                      )}
                    </button>
                    <button
                      onClick={handleUndo}
                      className="px-2 py-3 text-sm font-medium font-Mulish text-sm text-graphite hover:bg-primary-50 hover:text-primary-700 bg-white disabled:opacity-40 disabled:hover:bg-white disabled:hover:text-graphite group relative"
                      title="Undo Image"
                      disabled={undoStack.length === 0}
                    >
                      <div className="flex flex-col items-center justify-center">
                        <Undo2Icon className="h-6 w-6 group-hover:scale-110 transition-transform" />
                      </div>
                      <div className="absolute right-full mr-2 top-1/2 -translate-y-1/2 rounded-lg bg-white px-2.5 py-1 font-Mulish text-xs text-secondary shadow-[0_0_0_1px_var(--hairline)] opacity-0 group-hover:opacity-100 whitespace-nowrap pointer-events-none z-50">
                        Undo
                        <div className="hidden"></div>
                      </div>
                    </button>
                    <button
                      onClick={handleRedo}
                      className="px-2 py-3 text-sm font-medium font-Mulish text-sm text-graphite hover:bg-primary-50 hover:text-primary-700 bg-white disabled:opacity-40 disabled:hover:bg-white disabled:hover:text-graphite group relative"
                      title="Redo Image"
                      disabled={redoStack.length === 0}
                    >
                      <div className="flex flex-col items-center justify-center">
                        <Redo2Icon className="h-6 w-6 group-hover:scale-110 transition-transform" />
                      </div>
                      <div className="absolute right-full mr-2 top-1/2 -translate-y-1/2 rounded-lg bg-white px-2.5 py-1 font-Mulish text-xs text-secondary shadow-[0_0_0_1px_var(--hairline)] opacity-0 group-hover:opacity-100 whitespace-nowrap pointer-events-none z-50">
                        Redo
                        <div className="hidden"></div>
                      </div>
                    </button>
                    <button
                      onClick={handleCompareToggle}
                      disabled={
                        isProcessing ||
                        currentMediaUrl === null ||
                        undoStack.length === 0 ||
                        isMediaTypeVideo // Disable for video
                      }
                      className={`px-2 py-3 text-sm font-medium font-Mulish text-sm text-graphite hover:bg-primary-50 hover:text-primary-700 bg-white disabled:opacity-40 disabled:hover:bg-white disabled:hover:text-graphite group relative ${
                        isCompareMode
                          ? "bg-primary-50 text-primary-700 border-b-2 border-primary-500"
                          : ""
                      }`}
                      title="Compare Images"
                    >
                      <div className="flex flex-col items-center justify-center">
                        <Columns2Icon
                          className={`h-6 w-6 group-hover:scale-110 transition-transform`}
                        />
                      </div>
                      <div className="absolute right-full mr-2 top-1/2 -translate-y-1/2 rounded-lg bg-white px-2.5 py-1 font-Mulish text-xs text-secondary shadow-[0_0_0_1px_var(--hairline)] opacity-0 group-hover:opacity-100 whitespace-nowrap pointer-events-none z-50">
                        Compare
                        <div className="hidden"></div>
                      </div>
                    </button>
                    <button
                      onClick={() => handleSaveToGallery()}
                      disabled={
                        isProcessing ||
                        currentMediaUrl === null ||
                        isMediaTypeVideo
                      } // Disable for video
                      className="px-2 py-3 text-sm font-medium font-Mulish text-sm text-graphite hover:bg-primary-50 hover:text-primary-700 bg-white disabled:opacity-40 disabled:hover:bg-white disabled:hover:text-graphite group relative"
                      title="Save To Gallery"
                    >
                      <div className="flex flex-col items-center justify-center">
                        <ImageDownIcon className="h-6 w-6 group-hover:scale-110 transition-transform" />
                      </div>
                      <div className="absolute right-full mr-2 top-1/2 -translate-y-1/2 rounded-lg bg-white px-2.5 py-1 font-Mulish text-xs text-secondary shadow-[0_0_0_1px_var(--hairline)] opacity-0 group-hover:opacity-100 whitespace-nowrap pointer-events-none z-50">
                        Save To Gallery
                        <div className="hidden"></div>
                      </div>
                    </button>
                    <button
                      onClick={() => handleDownloadImage()}
                      disabled={isProcessing || currentMediaUrl === null}
                      className="px-2 py-3 text-sm font-medium font-Mulish text-sm text-graphite hover:bg-primary-50 hover:text-primary-700 bg-white disabled:opacity-40 disabled:hover:bg-white disabled:hover:text-graphite group relative"
                      title="Download Image"
                    >
                      <div className="flex flex-col items-center justify-center">
                        <DownloadIcon className="h-6 w-6 group-hover:scale-110 transition-transform" />
                      </div>
                      <div className="absolute right-full mr-2 top-1/2 -translate-y-1/2 rounded-lg bg-white px-2.5 py-1 font-Mulish text-xs text-secondary shadow-[0_0_0_1px_var(--hairline)] opacity-0 group-hover:opacity-100 whitespace-nowrap pointer-events-none z-50">
                        Download
                        <div className="hidden"></div>
                      </div>
                    </button>
                    <button
                      onClick={() => {
                        if (currentMediaUrl) {
                          // Use currentMediaUrl
                          // Exit crop mode if active
                          if (isCropping) {
                            setIsCropping(false);
                          }
                          // Disable chat mode
                          if (showChat) {
                            setShowChat(false);
                          }
                          // Save current media to history before removing (saving null)
                          saveMediaToHistory(null, false); // Pass false for isVideo as it's clearing
                          setCurrentMediaUrl(null);
                          setIsMediaTypeVideo(false); // Reset media type
                          // Disable compare mode when removing an image
                          if (isCompareMode) {
                            setIsCompareMode(false);
                          }
                          if (isCaptioning) {
                            setIsCaptioning(false);
                            setShowCaptionOptions(false);
                          }
                          // Clear the undo stack
                          setUndoStack([]);
                          setRedoStack([]);
                        }
                        setImageSettings(defaultGenerationSettings);
                      }}
                      className="rounded-b-2xl bg-white px-2 py-3 font-Mulish text-sm text-graphite hover:bg-primary-50 hover:text-primary-700 disabled:opacity-40 disabled:hover:bg-white disabled:hover:text-graphite group relative"
                      title="Clear All"
                    >
                      <div className="flex flex-col items-center justify-center">
                        <Trash2Icon className="h-6 w-6 group-hover:scale-110 transition-transform" />
                      </div>
                      <div className="absolute right-full mr-2 top-1/2 -translate-y-1/2 rounded-lg bg-white px-2.5 py-1 font-Mulish text-xs text-secondary shadow-[0_0_0_1px_var(--hairline)] opacity-0 group-hover:opacity-100 whitespace-nowrap pointer-events-none z-50">
                        Clear All
                        <div className="hidden"></div>
                      </div>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="flex w-full" style={{ height: containerHeight }}>
            <div className="flex flex-1 flex-col items-center justify-start overflow-y-auto px-4 pt-12">
              <div className="mb-8 flex flex-col items-center justify-center text-center">
                <h1 className="font-Mulish font-semibold text-4xl text-secondary">Studio</h1>
                <p className="mt-2 max-w-md font-Mulish text-sm text-graphite">
                  Remove a background, upscale, edit, or caption a photo.
                </p>
              </div>
              <div className="w-full max-w-lg">
                <SignInPrompt
                  title="Sign in to edit a photo"
                  message="Upload a photo to remove a background, upscale, edit, or caption it."
                  onSignIn={() => navigate("/signin")}
                />
              </div>
            </div>
            <div className="mr-4 flex shrink-0 flex-col">
              <div className="rounded-t-2xl bg-white px-3 py-2 text-center font-Mulish text-sm font-semibold text-secondary shadow-[0_0_0_1px_var(--hairline)]">
                Tools
              </div>
              <div className="flex flex-col divide-y divide-hairline rounded-b-2xl bg-white shadow-[0_0_0_1px_var(--hairline)]">
                {[
                  { label: "Remove background (4 credits)", icon: <WandSparklesIcon className="h-6 w-6" /> },
                  { label: "Change background", icon: <LayersIcon className="h-6 w-6" /> },
                  { label: "Upscale (48 credits)", icon: <ScalingIcon className="h-6 w-6" /> },
                  { label: "Resize (free)", icon: <ScalingIcon className="h-6 w-6" /> },
                  { label: "Crop (free)", icon: <CropIcon className="h-6 w-6" /> },
                  { label: "Caption (1 credit)", icon: <CaptionsIcon className="h-6 w-6" /> },
                  { label: "Chat (1 credit)", icon: <MessagesSquareIcon className="h-6 w-6" /> },
                ].map((tool) => (
                  <button
                    key={tool.label}
                    type="button"
                    onClick={() => navigate("/signin")}
                    className="group relative bg-white px-2 py-3 font-Mulish text-sm text-graphite hover:bg-primary-50 hover:text-primary-700"
                    title={tool.label}
                  >
                    <div className="flex flex-col items-center justify-center">{tool.icon}</div>
                    <div className="pointer-events-none absolute right-full top-1/2 z-50 mr-2 -translate-y-1/2 whitespace-nowrap rounded-lg bg-white px-2.5 py-1 font-Mulish text-xs text-secondary opacity-0 shadow-[0_0_0_1px_var(--hairline)] group-hover:opacity-100">
                      {tool.label}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
        {isProcessing && <ProcessingStatus />}
      </div>
      {showGalleryModal && (
        <GalleryModal
          setImage={handleGalleryImageSelect}
          setSelectedObj={setSelectedObj}
          setShowGalleryModal={setShowGalleryModal}
        />
      )}
      {showToastErrorMessage && (
        <div className="absolute top-4 left-0 right-0 flex justify-center z-30">
          <div
            id="toast-default"
            className="flex items-center w-full max-w-md p-4 text-gray-500 bg-white/95 backdrop-blur-sm rounded-lg shadow-xl border-l-4 border-red-500 animate-fade-in-down"
            role="alert"
          >
            <div className="inline-flex items-center justify-center flex-shrink-0 w-8 h-8 text-red-500 bg-red-100 rounded-lg">
              <svg
                className="w-5 h-5"
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5Zm3.707 11.793a1 1 0 1 1-1.414 1.414L10 11.414l-2.293 2.293a1 1 0 0 1-1.414-1.414L8.586 10 6.293 7.707a1 1 0 0 1 1.414-1.414L10 8.586l2.293-2.293a1 1 0 0 1 1.414 1.414L11.414 10l2.293 2.293a1 1 0 0 1-1.414 1.414L10 11.414z" />
              </svg>
              <span className="sr-only">Error icon</span>
            </div>
            <div className="flex flex-col mr-4">
              <div className="ms-3 text-base font-semibold text-red-500">
                {errorStatus}
              </div>
              <div className="ms-3 text-sm font-normal text-red-400">
                {errorMessage}
              </div>
            </div>
            <button
              type="button"
              className="ms-auto -mx-1.5 -my-1.5 bg-white/80 text-gray-400 hover:text-gray-900 rounded-lg focus:ring-2 focus:ring-gray-300 p-1.5 hover:bg-gray-100 inline-flex items-center justify-center h-8 w-8"
              data-dismiss-target="#toast-default"
              aria-label="Close"
              onClick={handleErrorClose}
            >
              <span className="sr-only">Close</span>
              <svg
                className="w-3 h-3"
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 14 14"
              >
                <path
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"
                />
              </svg>
            </button>
          </div>
        </div>
      )}
      {showToastSuccessMessage && (
        <div className="absolute top-4 left-0 right-0 flex justify-center z-30">
          <div
            id="toast-success"
            className="flex items-center w-full max-w-md p-4 text-gray-500 bg-white/95 backdrop-blur-sm rounded-lg shadow-xl border-l-4 border-green-500 animate-fade-in-down"
            role="alert"
          >
            <div className="inline-flex items-center justify-center flex-shrink-0 w-8 h-8 text-green-500 bg-green-100 rounded-lg">
              <svg
                className="w-5 h-5"
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5Zm3.707 8.207-4 4a1 1 0 0 1-1.414 0l-2-2a1 1 0 0 1 1.414-1.414L9 10.586l3.293-3.293a1 1 0 0 1 1.414 1.414Z" />
              </svg>
              <span className="sr-only">Success icon</span>
            </div>
            <div className="ms-3 text-sm font-semibold text-green-600">
              {successMessage}
            </div>
            <button
              type="button"
              className="ms-auto -mx-1.5 -my-1.5 bg-white/80 text-gray-400 hover:text-gray-900 rounded-lg focus:ring-2 focus:ring-gray-300 p-1.5 hover:bg-gray-100 inline-flex items-center justify-center h-8 w-8"
              data-dismiss-target="#toast-success"
              aria-label="Close"
              onClick={handleSuccessClose}
            >
              <span className="sr-only">Close</span>
              <svg
                className="w-3 h-3"
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 14 14"
              >
                <path
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"
                />
              </svg>
            </button>
          </div>
        </div>
      )}
      {showCaptionRequestResponse && <CaptionResponse />}
    </div>
  );
};

export default Studio;
